import { authApiClient } from "../../../services/authApiClient";
import { throwError } from "../../../utils/errorHandling";
import {
  IDepartment,
  IRole,
  IUpdatePassword,
  IUser,
  IUserValidation,
  IFunctionalityByDepartment,
  IFunctionality,
  IAssignFunctionality,
  IUserProfile,
  IUserProfileInput,
  IAssignFunctionalityDep,
  IDepartmentByFunctionality,
  IFactorySection,
} from "../model/user";
import { IStandardResponse } from "../../orders/models/order";
import { toast } from "react-toastify";

export enum EUserEndPoint {
  ALL_USER = "user/all_user",
  VALIDATE_USER = "user/validate/",
  USER = "user/",
  USER_PROFILE = "user/profile/",
  USER_BY_SECTION = "user/section/",
  UPDATE_PASSWORD = "auth/change_password/",
  RESET_PASSWORD = "auth/reset_password/",
  DEPARTMENT = "department/",
  FUNCTIONALITY_DEPARTMENT = "functionality/department/",
  ROLE = "role/",
  FUNCTIONALITY = "functionality/",
  ASSIGN_FUNCTIONALITY = "functionality/department/",
  UPDATE_PROFILE = "user/update_profile/",
  LOGOUT = "auth/logout/",
  DISABLE_USER = "user/disable/",
  ENABLE_USER = "user/enable/",
  DEPARTMENT_FUNCTIONALITY = "functionality/findDepartments/",
  FACTORY_SECTION = "factory_section/",
}

export const userApi = {
  getUsers: async (
    global_search?: string
  ): Promise<IStandardResponse<Array<IUser>>> => {
    try {
      const { data } = await authApiClient.get<IStandardResponse<Array<IUser>>>(
        EUserEndPoint.ALL_USER,
        {
          params: {
            global_search,
          },
        }
      );
      return data;
    } catch (error: any) {
      console.warn(error);
      throw new Error(error);
    }
  },

  updateUser: async (
    id_user: number,
    user: Partial<IUser>
  ): Promise<IStandardResponse<IUser>> => {
    try {
      const { data } = await authApiClient.patch<IStandardResponse<IUser>>(
        `${EUserEndPoint.USER}${id_user}`,
        user
      );
      if (!data) {
        throw new Error("No data returned from the server");
      }
      toast.success("User successfully updated.");
      return data;
    } catch (error: any) {
      toast.error(`${error.error.message}` || "Update user failed");
      throw new Error(error);
    }
  },

  updateProfile: async (
    id_user: number,
    payload: { user: Partial<IUser>; profile: IUserProfileInput | undefined }
  ): Promise<IStandardResponse<IUser>> => {
    try {
      const { data } = await authApiClient.patch<IStandardResponse<IUser>>(
        `${EUserEndPoint.UPDATE_PROFILE}${id_user}`,
        payload
      );
      if (!data) {
        throw new Error("No data returned from the server");
      }
      toast.success("User successfully updated.");
      return data;
    } catch (error: any) {
      toast.error(`${error.error.message}` || "Update user profile failed");
      throw new Error(error);
    }
  },

  disableUser: async (id_user: number): Promise<IStandardResponse<unknown>> => {
    try {
      const { data } = await authApiClient.get<IStandardResponse<IUser>>(
        `${EUserEndPoint.DISABLE_USER}${id_user}`
      );
      if (!data) {
        throw new Error("No data returned from the server");
      }
      toast.success("User status successfully updated.");
      return data;
    } catch (error: any) {
      toast.error(
        `${error.error.message}` || "Error occured on updating user status"
      );
      throw new Error(throwError(error));
    }
  },

  enableUser: async (id_user: number): Promise<IStandardResponse<unknown>> => {
    try {
      const { data } = await authApiClient.get<IStandardResponse<IUser>>(
        `${EUserEndPoint.ENABLE_USER}${id_user}`
      );
      if (!data) {
        throw new Error("No data returned from the server");
      }
      toast.success("User status successfully updated.");
      return data;
    } catch (error: any) {
      toast.error(
        `${error.error.message}` || "Error occured on updating user status"
      );
      throw new Error(throwError(error));
    }
  },

  getUserById: async (id_user: number): Promise<IStandardResponse<IUser>> => {
    try {
      const { data } = await authApiClient.get<IStandardResponse<IUser>>(
        `${EUserEndPoint.USER}${id_user}`
      );
      return data;
    } catch (error: any) {
      console.warn(error);
      throw new Error(error);
    }
  },

  getUserProfile: async (
    id_user: number
  ): Promise<IStandardResponse<IUserProfile>> => {
    try {
      const { data } = await authApiClient.get<IStandardResponse<IUserProfile>>(
        `${EUserEndPoint.USER_PROFILE}${id_user}`
      );
      return data;
    } catch (error: any) {
      console.warn(error);
      throw new Error(error);
    }
  },
  getUserBySection:async(   id_section: number):Promise<IStandardResponse<IUser[]>> => {
    try {
      const { data } = await authApiClient.get<IStandardResponse<IUser[]>>(
        `${EUserEndPoint.USER_BY_SECTION}${id_section}`
      );
      return data;
    } catch (error: any) {
      console.warn(error);
      throw new Error(error);
    }
  },
  updatePassword: async (
    id_user: number,
    passwordData: IUpdatePassword
  ): Promise<IStandardResponse<IUser>> => {
    try {
      // Input validation
      if (!id_user) throw new Error("User ID is required");
      if (!passwordData.currentPassword || !passwordData.newPassword) {
        throw new Error("Current and new passwords are required");
      }

      // If confirmPassword is provided, validate it matches
      if (
        passwordData.confirmNewPassword !== undefined &&
        passwordData.confirmNewPassword !== passwordData.newPassword
      ) {
        throw new Error("New password and confirmation do not match");
      }

      const { data } = await authApiClient.patch<IStandardResponse<IUser>>(
        `${EUserEndPoint.UPDATE_PASSWORD}${id_user}`,
        passwordData
      );

      toast.success("Password successfully updated.");
      return data;
    } catch (error: any) {
      console.warn(error);
      toast.error(`${error.error.message}` || "Update password failed");
      throw new Error(error);
    }
  },

  resetPassword: async (matricule: string): Promise<IStandardResponse<{ message: string }>> => {
    try {
      const { data } = await authApiClient.patch<IStandardResponse<{ message: string }>>(
        `${EUserEndPoint.RESET_PASSWORD}${matricule}`
      );
      toast.success(`Password reset successfully for ${matricule}`);
      return data;
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Reset password failed");
      throw new Error(error);
    }
  },

  validateUser: async (
    id_user: number,
    validationData: IUserValidation
  ): Promise<IStandardResponse<{ message: string }>> => {
    try {
      const { data } = await authApiClient.patch<
        IStandardResponse<{ message: string }>
      >(`${EUserEndPoint.VALIDATE_USER}${id_user}`, validationData);
      toast.success("User has been validated");
      return data;
    } catch (error: any) {
      toast.error(`${error.error.message}` || "Error on validating user");
      throw new Error(error);
    }
  },

  getRoles: async (): Promise<IStandardResponse<Array<IRole>>> => {
    try {
      const { data } = await authApiClient.get<IStandardResponse<Array<IRole>>>(
        EUserEndPoint.ROLE
      );
      return data;
    } catch (error: any) {
      console.warn(error);
      throw new Error(error);
    }
  },

  getFunctionalityByDepartment: async (
    id_department: number
  ): Promise<IStandardResponse<Array<IFunctionalityByDepartment>>> => {
    try {
      const { data } = await authApiClient.get<
        IStandardResponse<Array<IFunctionalityByDepartment>>
      >(`${EUserEndPoint.FUNCTIONALITY_DEPARTMENT}${id_department}`);
      return data;
    } catch (error: any) {
      console.warn(error);
      throw new Error(error);
    }
  },

  updateAssignFunctionality: async (
    id_department: number,
    funcsData: IAssignFunctionalityDep[]
  ): Promise<IStandardResponse<unknown>> => {
    try {
      const { data } = await authApiClient.patch<IStandardResponse<unknown>>(
        `${EUserEndPoint.ASSIGN_FUNCTIONALITY}${id_department}`,
        funcsData
      );
      toast.success("Functionality updated.");
      return data;
    } catch (error: any) {
      console.warn(error);
      toast.error(
        `${error.error.message}` || "Error on assigning functionality"
      );
      throw new Error(error);
    }
  },

  getFunctionalities: async (): Promise<
    IStandardResponse<Array<IFunctionality>>
  > => {
    try {
      const { data } = await authApiClient.get<
        IStandardResponse<Array<IFunctionality>>
      >(EUserEndPoint.FUNCTIONALITY);
      return data;
    } catch (error: any) {
      console.warn(error);
      throw new Error(error);
    }
  },

  getDepartments: async (): Promise<IStandardResponse<Array<IDepartment>>> => {
    try {
      const { data } = await authApiClient.get<
        IStandardResponse<Array<IDepartment>>
      >(EUserEndPoint.DEPARTMENT);
      return data;
    } catch (error: any) {
      console.warn(error);
      throw new Error(error);
    }
  },

  assignFunctionality: async (
    id_department: number,
    funcsData: IAssignFunctionality[]
  ): Promise<IStandardResponse<unknown>> => {
    try {
      const { data } = await authApiClient.post<IStandardResponse<unknown>>(
        `${EUserEndPoint.ASSIGN_FUNCTIONALITY}${id_department}`,
        funcsData
      );
      toast.success("Functionality assigned in department");
      return data;
    } catch (error: any) {
      console.warn(error);
      toast.error(
        `${error.error.message}` || "Error on assigning functionality"
      );
      throw new Error(error);
    }
  },

  removeFunctionality: async (
    id_department: number,
    funcsData: IAssignFunctionality[]
  ): Promise<IStandardResponse<{ success: boolean; timestamp: boolean }>> => {
    try {
      const { data } = await authApiClient.delete<
        IStandardResponse<{ success: boolean; timestamp: boolean }>
      >(`${EUserEndPoint.ASSIGN_FUNCTIONALITY}${id_department}`, {
        data: funcsData,
      });
      toast.success("Functionality removed");
      return data;
    } catch (error: any) {
      toast.error(`${error.error.message}` || "Error on remove functionality");
      throw new Error(error);
    }
  },

  logout: async (): Promise<IStandardResponse<unknown>> => {
    try {
      const { data } = await authApiClient.post<IStandardResponse<unknown>>(
        `${EUserEndPoint.LOGOUT}`
      );
      return data;
    } catch (error: any) {
      console.warn(error);
      throw new Error(error);
    }
  },

  getDepartmentByFunctionality: async (
    id_functionality: number
  ): Promise<IStandardResponse<Array<IDepartmentByFunctionality>>> => {
    try {
      const { data } = await authApiClient.get<
        IStandardResponse<Array<IDepartmentByFunctionality>>
      >(`${EUserEndPoint.DEPARTMENT_FUNCTIONALITY}${id_functionality}`);
      return data;
    } catch (error: any) {
      console.warn(error);
      throw new Error(error);
    }
  },

  getFactorySection: async (): Promise<IStandardResponse<Array<IFactorySection>>> => {
    try {
      const { data } = await authApiClient.get<
        IStandardResponse<Array<IFactorySection>>
      >(EUserEndPoint.FACTORY_SECTION);
      return data;
    } catch (error: any) {
      console.warn(error);
      throw new Error(error);
    }
  },
};
